/**
 * 單筆報價單「自訂匯率」的共用邏輯（需求 R97）。
 *
 * 為什麼抽成共用檔：QuoteCreatePage.js 與 QuoteEditPage.js 是兩份各自獨立的程式碼
 * （同一段匯率邏輯歷史上被複製了兩份），若把驗證與取值規則各寫一次，日後一定會漂移成
 * 「新建頁能輸入 3 位小數、編輯頁只能 2 位」。兩頁都呼叫這裡，規則只有一份定義（AC9）。
 *
 * PM 2026-07-31 逐字拍板：
 *   Q1 權限：(B) 所有能編輯該報價單的人（含業務）皆可解鎖 → 本檔不做任何角色判斷，
 *            呼叫端只以既有的「可編輯／isLocked」條件把關。
 *   Q2 幣別切換：(A) 清除自訂值並回到新幣別的全域匯率＋回到上鎖 → 呼叫端在 setCurrency 時清狀態。
 *   Q3 還原機制：比照單價那顆紅色驚嘆號，點擊即還原成全域匯率。
 *   Q4 數值格式：允許小數位數「依照該幣別的設定」→ 以該幣別在帳號管理（/api/exchange-rates）
 *            設定的全域匯率值本身的小數位數為準，見 getRateDecimals()。
 *   Q5 既有報價單回溯：(A) 只有標記為自訂匯率的報價單鎖定存檔值，其餘維持跟隨全域匯率。
 *   Q6 其他頁面：本來就沒有顯示此欄位的頁面不因本次調整多做標示 → 本檔不提供任何標示字串。
 */

/** 報價幣別 → /api/exchange-rates 的 key（與兩頁原本的 getCurrentRate 對應表一致）。 */
export const RATE_KEY_BY_CURRENCY = {
  HKD: 'hkd_quote',
  USD: 'usd_quote',
  EUR: 'eur_quote',
};

/**
 * 小數位數的下限。
 *
 * Q4 的「依照該幣別的設定」照字面是看該幣別全域匯率值的小數位數，但全域匯率允許存整數
 * （預設 eur_quote = 36），若完全照字面就會變成「歐元自訂匯率只能填整數」，等於把功能做死。
 * 因此取 max(全域匯率小數位數, 2)：幣別設定較精細時跟著放寬，設定是整數時仍保有匯率慣用的
 * 兩位小數。此下限已在票上向 PM 標示，如需改為嚴格照字面只要調這個常數。
 */
export const MIN_RATE_DECIMALS = 2;

/** 取某幣別目前的「全域匯率」（TWD 固定為 1，查無設定時退回 1，與原 getCurrentRate 行為相同）。 */
export function getGlobalRate(currency, exchangeRates) {
  if (currency === 'TWD') return 1;
  const key = RATE_KEY_BY_CURRENCY[currency];
  if (!key) return 1;
  return (exchangeRates || {})[key] || 1;
}

/** 數出一個數字或數字字串的小數位數（'4.05' → 2、36 → 0；非數字回 0）。 */
export function countDecimals(value) {
  const text = String(value ?? '').trim();
  if (!/^\d*\.?\d*$/.test(text)) return 0;
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : text.length - dot - 1;
}

/** 該幣別允許的自訂匯率小數位數（Q4：依全域匯率設定，且不低於 MIN_RATE_DECIMALS）。 */
export function getRateDecimals(currency, exchangeRates) {
  return Math.max(countDecimals(getGlobalRate(currency, exchangeRates)), MIN_RATE_DECIMALS);
}

/** 輸入過程允許的字元（只擋非法字元，不在打字途中判斷位數，避免使用者打不出小數點）。 */
export function isRateInputAllowed(text) {
  return text === '' || /^\d*\.?\d*$/.test(text);
}

/**
 * 上鎖（套用）時的驗證（AC12）。
 * 回傳 { ok: true, value } 或 { ok: false, message }；訊息直接顯示給使用者。
 */
export function validateCustomRate(raw, currency, exchangeRates) {
  const text = String(raw ?? '').trim();
  if (text === '') return { ok: false, message: '請輸入匯率' };
  if (!isRateInputAllowed(text) || text === '.') {
    return { ok: false, message: '匯率只能輸入數字' };
  }
  const value = Number(text);
  if (!Number.isFinite(value) || value <= 0) {
    return { ok: false, message: '匯率必須大於 0' };
  }
  const decimals = getRateDecimals(currency, exchangeRates);
  if (countDecimals(text) > decimals) {
    return {
      ok: false,
      message: `${currency} 匯率最多輸入 ${decimals} 位小數（依帳號管理的全域匯率設定）`,
    };
  }
  return { ok: true, value };
}
